import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, BookOpen, Layers, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function ClassManagement() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [showClassDialog, setShowClassDialog] = useState(false);
  const [showSubjectDialog, setShowSubjectDialog] = useState(false);
  const [classForm, setClassForm] = useState({ name: '', division: '', grade: '', section: '', class_teacher_id: '', room: '', capacity: 30 });
  const [subjectForm, setSubjectForm] = useState({ name: '', code: '', division: '', grades: [], teacher_ids: [] });
  const [editingClass, setEditingClass] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [classPhotoFile, setClassPhotoFile] = useState(null);

  const loadAll = () => {
    api.get('/admin/classes').then(r => setClasses(r.data)).catch(() => {});
    api.get('/admin/subjects').then(r => setSubjects(r.data)).catch(() => {});
    api.get('/admin/divisions').then(r => setDivisions(r.data)).catch(() => {});
    api.get('/admin/teachers').then(r => setTeachers(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const classGrades = divisions.find(d => d.name === classForm.division)?.grades || [];

  const saveClass = async () => {
    try {
      let photoUrl = classForm.photo_url || '';
      if (classPhotoFile) {
        const fd = new FormData();
        fd.append('file', classPhotoFile);
        try {
          const { data: fileData } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          photoUrl = fileData.url || fileData.path || '';
        } catch { toast.error('Photo upload failed'); }
      }
      const payload = { ...classForm, photo_url: photoUrl };
      if (editingClass) { await api.put(`/admin/classes/${editingClass}`, payload); toast.success('Class updated'); }
      else { await api.post('/admin/classes', payload); toast.success('Class created'); }
      setShowClassDialog(false); setEditingClass(null); setClassPhotoFile(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveSubject = async () => {
    try {
      if (editingSubject) { await api.put(`/admin/subjects/${editingSubject}`, subjectForm); toast.success('Subject updated'); }
      else { await api.post('/admin/subjects', subjectForm); toast.success('Subject created'); }
      setShowSubjectDialog(false); setEditingSubject(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div data-testid="class-management">
      <h1 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-2"><Layers className="w-6 h-6" /> Classes & Subjects</h1>
      <Tabs defaultValue="classes">
        <TabsList className="mb-6">
          <TabsTrigger value="classes">Classes</TabsTrigger>
          <TabsTrigger value="subjects">Subjects</TabsTrigger>
        </TabsList>

        <TabsContent value="classes">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{classes.length} active classes</p>
            <Button onClick={() => { setClassForm({ name: '', division: '', grade: '', section: '', class_teacher_id: '', room: '', capacity: 30 }); setEditingClass(null); setShowClassDialog(true); }} className="bg-[#1B2A4A]" data-testid="add-class-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Class
            </Button>
          </div>
          <div className="bg-white rounded-xl border shadow-sm">
            <Table>
              <TableHeader><TableRow className="data-table"><TableHead>Class</TableHead><TableHead>Division</TableHead><TableHead>Grade</TableHead><TableHead>Room</TableHead><TableHead>Teacher</TableHead><TableHead>Students</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {classes.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="font-semibold">{c.name}</TableCell>
                    <TableCell>{c.division}</TableCell>
                    <TableCell>{c.grade}</TableCell>
                    <TableCell>{c.room}</TableCell>
                    <TableCell className="text-sm">{c.class_teacher_name || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{c.student_count || 0}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => { setClassForm(c); setEditingClass(c.id); setShowClassDialog(true); }}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={async () => { if(window.confirm('Delete?')){ await api.delete(`/admin/classes/${c.id}`); toast.success('Deleted'); loadAll(); }}}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="subjects">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{subjects.length} subjects</p>
            <Button onClick={() => { setSubjectForm({ name: '', code: '', division: '', grades: [], teacher_ids: [] }); setEditingSubject(null); setShowSubjectDialog(true); }} className="bg-[#1B2A4A]" data-testid="add-subject-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Subject
            </Button>
          </div>
          <div className="bg-white rounded-xl border shadow-sm">
            <Table>
              <TableHeader><TableRow className="data-table"><TableHead>Subject</TableHead><TableHead>Code</TableHead><TableHead>Division</TableHead><TableHead>Grades</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {subjects.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="font-semibold">{s.name}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono">{s.code}</Badge></TableCell>
                    <TableCell>{s.division || 'All'}</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">{(s.grades||[]).map(g => <Badge key={g} variant="secondary" className="text-xs">{g}</Badge>)}</div></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => { setSubjectForm(s); setEditingSubject(s.id); setShowSubjectDialog(true); }}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={async () => { if(window.confirm('Delete?')){ await api.delete(`/admin/subjects/${s.id}`); toast.success('Deleted'); loadAll(); }}}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Class Dialog */}
      <Dialog open={showClassDialog} onOpenChange={setShowClassDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingClass ? 'Edit Class' : 'Add Class'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Class Name</Label><Input value={classForm.name} onChange={e => setClassForm({...classForm, name: e.target.value})} placeholder="e.g., Grade 4A" data-testid="class-name-input" /></div>
            <div><Label>Division</Label><Select value={classForm.division} onValueChange={v => setClassForm({...classForm, division: v, grade: ''})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{divisions.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Grade</Label><Select value={classForm.grade} onValueChange={v => setClassForm({...classForm, grade: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{classGrades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Room</Label><Input value={classForm.room} onChange={e => setClassForm({...classForm, room: e.target.value})} /></div>
            <div><Label>Capacity</Label><Input type="number" value={classForm.capacity} onChange={e => setClassForm({...classForm, capacity: parseInt(e.target.value)||30})} /></div>
            <div className="col-span-2"><Label>Class Teacher</Label><Select value={classForm.class_teacher_id || 'none'} onValueChange={v => setClassForm({...classForm, class_teacher_id: v === 'none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Select Teacher" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-2">
              <Label className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Class Photo (optional)</Label>
              <Input type="file" accept=".jpg,.jpeg,.png" onChange={e => setClassPhotoFile(e.target.files[0])} className="mt-1" data-testid="class-photo-input" />
              {(classForm.photo_url || classPhotoFile) && (
                <div className="mt-2">
                  <img src={classPhotoFile ? URL.createObjectURL(classPhotoFile) : `${process.env.REACT_APP_BACKEND_URL}/api/public/files/${classForm.photo_url}`} alt="Class" className="w-full h-24 object-cover rounded-lg border" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowClassDialog(false)}>Cancel</Button><Button onClick={saveClass} className="bg-[#1B2A4A]" data-testid="save-class-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subject Dialog */}
      <Dialog open={showSubjectDialog} onOpenChange={setShowSubjectDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingSubject ? 'Edit Subject' : 'Add Subject'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Subject Name</Label><Input value={subjectForm.name} onChange={e => setSubjectForm({...subjectForm, name: e.target.value})} data-testid="subject-name-input" /></div>
            <div><Label>Code</Label><Input value={subjectForm.code} onChange={e => setSubjectForm({...subjectForm, code: e.target.value})} placeholder="e.g., MATH" /></div>
            <div className="col-span-2"><Label>Division</Label><Select value={subjectForm.division || 'all'} onValueChange={v => setSubjectForm({...subjectForm, division: v === 'all' ? '' : v})}><SelectTrigger><SelectValue placeholder="All Divisions" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{divisions.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSubjectDialog(false)}>Cancel</Button><Button onClick={saveSubject} className="bg-[#1B2A4A]" data-testid="save-subject-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
